import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
  type CSSProperties,
} from 'react';

/**
 * Priority levels for announcements.
 * - polite: Waits for current speech to finish (default)
 * - assertive: Interrupts current speech (use for errors/urgent updates)
 */
type AnnouncePriority = 'polite' | 'assertive';

interface AnnounceContextType {
  /**
   * Push an announcement to the ARIA live region for screen readers.
   * @param message - Text to announce
   * @param priority - 'polite' (default) or 'assertive' for urgent messages
   */
  announce: (message: string, priority?: AnnouncePriority) => void;
}

const AnnounceContext = createContext<AnnounceContextType | undefined>(
  undefined
);

/**
 * Hook to push announcements to the AccessibleAnnouncer live region.
 *
 * Common use cases:
 * - New message arrives: announce("New message from Agent")
 * - Agent typing: announce("Agent is typing...")
 * - Agent stops typing: announce("Agent finished responding")
 * - Error: announce("Error: failed to send message", "assertive")
 *
 * @example
 * const { announce } = useAnnounce();
 * announce('New message received from assistant');
 * announce('Connection lost', 'assertive');
 */
const noopAnnounce: AnnounceContextType = { announce: () => {} };

export function useAnnounce(): AnnounceContextType {
  const context = useContext(AnnounceContext);
  // Return a no-op outside the provider so components are safe to render in
  // tests and lightweight wrappers that omit AccessibleAnnouncer.
  return context ?? noopAnnounce;
}

interface AccessibleAnnouncerProps {
  children: ReactNode;
  /**
   * Duration in ms before clearing the announcement.
   * Prevents stale content from being re-read. Default: 5000ms
   */
  clearDelay?: number;
}

/**
 * AccessibleAnnouncer provides ARIA live regions for screen reader announcements.
 *
 * Renders two visually-hidden live regions (polite and assertive) that screen
 * readers monitor for dynamic content updates. Children can use the useAnnounce()
 * hook to push announcements.
 *
 * Place this component high in the tree (e.g., wrapping the app or chat area).
 *
 * @example
 * <AccessibleAnnouncer>
 *   <ChatInterface />
 * </AccessibleAnnouncer>
 */
const AccessibleAnnouncer = ({
  children,
  clearDelay = 5000,
}: AccessibleAnnouncerProps) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const idCounter = useRef(0);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announce = useCallback(
    (message: string, priority: AnnouncePriority = 'polite') => {
      if (!message.trim()) return;

      idCounter.current += 1;

      if (priority === 'assertive') {
        // Clear then set to ensure screen readers detect the change
        setAssertiveMessage('');
        requestAnimationFrame(() => {
          setAssertiveMessage(message);
        });
      } else {
        setPoliteMessage('');
        requestAnimationFrame(() => {
          setPoliteMessage(message);
        });
      }

      // Auto-clear after delay to prevent stale announcements
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
      }
      clearTimerRef.current = setTimeout(() => {
        setPoliteMessage('');
        setAssertiveMessage('');
      }, clearDelay);
    },
    [clearDelay]
  );

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  // Visually hidden styles that keep content accessible to screen readers
  const visuallyHiddenStyles: CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  return (
    <AnnounceContext.Provider value={{ announce }}>
      {children}

      {/* Polite live region - waits for current speech to finish */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={visuallyHiddenStyles}
      >
        {politeMessage}
      </div>

      {/* Assertive live region - interrupts current speech for urgent updates */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={visuallyHiddenStyles}
      >
        {assertiveMessage}
      </div>
    </AnnounceContext.Provider>
  );
};

export default AccessibleAnnouncer;
